import React, { useState, useEffect, useRef } from "react";
import { X, Save, Calendar, Search } from "lucide-react";
import * as roldanApi from "../../../../../../ApiCalls/taktikApiCalls/roldanApi";
import { getColombiaDateString } from "../../../../../../utils/dateUtils";

const PreManifiestoForm = ({
  mantisBusinessId,
  mantisBusinessUserId,
  token,
  preManifiestoId,
  onClose,
  onSuccess,
  hideEstado = false,
}) => {
  const [formData, setFormData] = useState({
    fecha_viaje: getColombiaDateString(),
    origen: "",
    origen_id: "",
    destino: "",
    destino_id: "",
    estado: "Abierto",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dateInputRef = useRef(null);
  
  // City search states
  const [origenSearchTerm, setOrigenSearchTerm] = useState("");
  const [origenSearchResults, setOrigenSearchResults] = useState([]);
  const [showOrigenDropdown, setShowOrigenDropdown] = useState(false);
  const [destinoSearchTerm, setDestinoSearchTerm] = useState("");
  const [destinoSearchResults, setDestinoSearchResults] = useState([]);
  const [showDestinoDropdown, setShowDestinoDropdown] = useState(false);
  const [searchingOrigen, setSearchingOrigen] = useState(false);
  const [searchingDestino, setSearchingDestino] = useState(false);
  
  // Debounce timers
  const origenSearchTimeoutRef = useRef(null);
  const destinoSearchTimeoutRef = useRef(null);

  useEffect(() => {
    if (preManifiestoId) {
      loadPreManifiesto();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preManifiestoId]);

  const loadPreManifiesto = async () => {
    try {
      const res = await roldanApi.getPreManifiestoById(mantisBusinessId, token, preManifiestoId);
      if (res?.success && res.data) {
        const data = res.data;
        setFormData({
          fecha_viaje: data.fecha_viaje ? data.fecha_viaje.split("T")[0] : "",
          origen: data.origen || "",
          origen_id: data.origen_id || "",
          destino: data.destino || "",
          destino_id: data.destino_id || "",
          estado: data.estado || "Abierto",
        });
        // Initialize search terms when loading pre-manifiesto
        if (data.origen) {
          setOrigenSearchTerm(data.origen);
        }
        if (data.destino) {
          setDestinoSearchTerm(data.destino);
        }
      }
    } catch (error) {
      console.error("Error loading pre-manifiesto:", error);
      setError("Error al cargar el pre-manifiesto");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Seleccionar fecha";
    
    // If dateString is already in YYYY-MM-DD format, parse it directly without timezone conversion
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      const monthNames = [
        'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
        'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
      ];
      return `${parseInt(day)} de ${monthNames[parseInt(month) - 1]} de ${year}`;
    }
    
    // Fallback for other date formats
    const date = new Date(dateString);
    // Use UTC methods to avoid timezone conversion issues
    const year = date.getUTCFullYear();
    const month = date.getUTCMonth();
    const day = date.getUTCDate();
    const monthNames = [
      'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
      'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'
    ];
    return `${day} de ${monthNames[month]} de ${year}`;
  };

  const handleDateButtonClick = () => {
    dateInputRef.current?.showPicker?.() || dateInputRef.current?.click();
  };

  // City search functions
  const searchCiudades = async (searchTerm, type) => {
    if (!searchTerm || searchTerm.trim().length < 2) {
      if (type === "origen") {
        setOrigenSearchResults([]);
        setShowOrigenDropdown(false);
      } else {
        setDestinoSearchResults([]);
        setShowDestinoDropdown(false);
      }
      return;
    }

    if (type === "origen") {
      setSearchingOrigen(true);
    } else {
      setSearchingDestino(true);
    }

    try {
      const res = await roldanApi.searchCiudades(mantisBusinessId, token, searchTerm.trim());
      if (res?.success && res.data) {
        if (type === "origen") {
          setOrigenSearchResults(res.data);
          setShowOrigenDropdown(true);
        } else {
          setDestinoSearchResults(res.data);
          setShowDestinoDropdown(true);
        }
      } else {
        if (type === "origen") {
          setOrigenSearchResults([]);
          setShowOrigenDropdown(false);
        } else {
          setDestinoSearchResults([]);
          setShowDestinoDropdown(false);
        }
      }
    } catch (error) {
      console.error("Error searching ciudades:", error);
      if (type === "origen") {
        setOrigenSearchResults([]);
        setShowOrigenDropdown(false);
      } else {
        setDestinoSearchResults([]);
        setShowDestinoDropdown(false);
      }
    } finally {
      if (type === "origen") {
        setSearchingOrigen(false);
      } else {
        setSearchingDestino(false);
      }
    }
  };

  const handleOrigenSearch = (value) => {
    setOrigenSearchTerm(value);
    
    // Clear previous timeout
    if (origenSearchTimeoutRef.current) {
      clearTimeout(origenSearchTimeoutRef.current);
    }
    
    if (value.trim().length >= 2) {
      // Debounce search by 300ms
      origenSearchTimeoutRef.current = setTimeout(() => {
        searchCiudades(value, "origen");
      }, 300);
    } else {
      setOrigenSearchResults([]);
      setShowOrigenDropdown(false);
    }
  };

  const handleDestinoSearch = (value) => {
    setDestinoSearchTerm(value);
    
    // Clear previous timeout
    if (destinoSearchTimeoutRef.current) {
      clearTimeout(destinoSearchTimeoutRef.current);
    }
    
    if (value.trim().length >= 2) {
      // Debounce search by 300ms
      destinoSearchTimeoutRef.current = setTimeout(() => {
        searchCiudades(value, "destino");
      }, 300);
    } else {
      setDestinoSearchResults([]);
      setShowDestinoDropdown(false);
    }
  };

  const selectOrigen = (ciudad) => {
    setFormData({
      ...formData,
      origen: ciudad.NOMBRE.trim(),
      origen_id: ciudad.CIUDAD,
    });
    setOrigenSearchTerm(ciudad.NOMBRE.trim());
    setShowOrigenDropdown(false);
    setOrigenSearchResults([]);
  };

  const selectDestino = (ciudad) => {
    setFormData({
      ...formData,
      destino: ciudad.NOMBRE.trim(),
      destino_id: ciudad.CIUDAD,
    });
    setDestinoSearchTerm(ciudad.NOMBRE.trim());
    setShowDestinoDropdown(false);
    setDestinoSearchResults([]);
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.city-search-container')) {
        setShowOrigenDropdown(false);
        setShowDestinoDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (origenSearchTimeoutRef.current) {
        clearTimeout(origenSearchTimeoutRef.current);
      }
      if (destinoSearchTimeoutRef.current) {
        clearTimeout(destinoSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.fecha_viaje) {
      setError("La fecha de viaje es requerida");
      return;
    }

    // Ensure fecha_viaje is in YYYY-MM-DD format (date only, no time)
    // The date input already provides this format, but we ensure it's clean
    let fechaViaje = formData.fecha_viaje;
    if (fechaViaje) {
      // If it's a Date object, format it as YYYY-MM-DD using UTC to avoid timezone issues
      if (fechaViaje instanceof Date) {
        const year = fechaViaje.getUTCFullYear();
        const month = String(fechaViaje.getUTCMonth() + 1).padStart(2, '0');
        const day = String(fechaViaje.getUTCDate()).padStart(2, '0');
        fechaViaje = `${year}-${month}-${day}`;
      } else if (typeof fechaViaje === 'string') {
        // Remove any time component if present
        fechaViaje = fechaViaje.split('T')[0].split(' ')[0];
      }
    }

    setLoading(true);
    try {
      let res;
      const submitData = {
        ...formData,
        fecha_viaje: fechaViaje
      };
      
      if (preManifiestoId) {
        res = await roldanApi.updatePreManifiesto(
          mantisBusinessId,
          token,
          preManifiestoId,
          submitData
        );
      } else {
        res = await roldanApi.createPreManifiesto(
          mantisBusinessId,
          mantisBusinessUserId,
          token,
          submitData
        );
      }

      if (res?.success) {
        if (onSuccess) onSuccess();
        if (onClose) onClose();
      } else {
        setError(res?.message || "Error al guardar el pre-manifiesto");
      }
    } catch (error) {
      console.error("Error saving pre-manifiesto:", error);
      setError("Error al guardar el pre-manifiesto");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4">
      <div className="w-full max-w-2xl bg-white rounded-xl shadow-2xl border border-gray-200 max-h-[90vh] overflow-y-auto"
        onClick={(e) => {
          // Close dropdowns when clicking outside
          if (!e.target.closest('.city-search-container')) {
            setShowOrigenDropdown(false);
            setShowDestinoDropdown(false);
          }
        }}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">
            {preManifiestoId ? "Editar Pre-Manifiesto" : "Nuevo Pre-Manifiesto"}
          </h2>
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-200 hover:bg-gray-100"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 sm:p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Fecha de Viaje <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  ref={dateInputRef}
                  type="date"
                  name="fecha_viaje"
                  value={formData.fecha_viaje}
                  onChange={handleChange}
                  required
                  className="absolute opacity-0 w-0 h-0 pointer-events-none"
                />
                <button
                  type="button"
                  onClick={handleDateButtonClick}
                  className={`w-full px-4 py-2.5 border rounded-lg text-left flex items-center justify-between transition-colors ${
                    formData.fecha_viaje
                      ? "border-gray-300 bg-white text-gray-900 hover:border-blue-400"
                      : "border-gray-300 bg-gray-50 text-gray-500 hover:border-blue-400 hover:bg-white"
                  } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <span className="flex items-center gap-2">
                    <Calendar size={18} className="text-gray-400" />
                    <span>{formatDate(formData.fecha_viaje)}</span>
                  </span>
                </button>
              </div>
            </div>

            <div className="city-search-container">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Origen <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <Search size={16} className="text-gray-400 ml-3" />
                  <input
                    type="text"
                    className="w-full px-3 py-2 outline-none rounded-lg"
                    value={origenSearchTerm || formData.origen || ""}
                    onChange={(e) => handleOrigenSearch(e.target.value)}
                    onFocus={() => {
                      if (origenSearchResults.length > 0) {
                        setShowOrigenDropdown(true);
                      }
                    }}
                    placeholder="Buscar ciudad de origen..."
                    required
                  />
                  {searchingOrigen && (
                    <div className="px-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>
                {showOrigenDropdown && origenSearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {origenSearchResults.map((ciudad, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectOrigen(ciudad)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {ciudad.NOMBRE.trim()}
                        </div>
                        <div className="text-xs text-gray-500">
                          Código: {ciudad.CIUDAD}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="city-search-container">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Destino <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="flex items-center border border-gray-200 rounded-lg">
                  <Search size={16} className="text-gray-400 ml-3" />
                  <input
                    type="text"
                    className="w-full px-3 py-2 outline-none rounded-lg"
                    value={destinoSearchTerm || formData.destino || ""}
                    onChange={(e) => handleDestinoSearch(e.target.value)}
                    onFocus={() => {
                      if (destinoSearchResults.length > 0) {
                        setShowDestinoDropdown(true);
                      }
                    }}
                    placeholder="Buscar ciudad de destino..."
                    required
                  />
                  {searchingDestino && (
                    <div className="px-3">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900"></div>
                    </div>
                  )}
                </div>
                {showDestinoDropdown && destinoSearchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    {destinoSearchResults.map((ciudad, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectDestino(ciudad)}
                        className="w-full text-left px-4 py-2 hover:bg-gray-100 border-b border-gray-100 last:border-b-0"
                      >
                        <div className="font-medium text-gray-900">
                          {ciudad.NOMBRE.trim()}
                        </div>
                        <div className="text-xs text-gray-500">
                          Código: {ciudad.CIUDAD}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {preManifiestoId && !hideEstado && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado
                </label>
                <select
                  name="estado"
                  value={formData.estado}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="Abierto">Abierto</option>
                  <option value="Cerrado">Cerrado</option>
                  <option value="Manifestado">Manifestado</option>
                </select>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-2 mt-6 pt-4 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-200 rounded-lg text-gray-800 hover:bg-gray-50 w-full sm:w-auto"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50 w-full sm:w-auto"
            >
              <Save size={16} />
              {loading ? "Guardando..." : preManifiestoId ? "Actualizar" : "Crear"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PreManifiestoForm;

